import {ModalState} from "../../../types/modals";
import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {api} from "../../api";
import {PlainDetails} from "../../../types/admin";
import {toastSuccess} from "../admin";
import {updateUser} from "../adminUserView";


export type UserModalState = ModalState & {
    user_id: string | null
    first_name: string
    last_name: string
    email: string
    is_staff: boolean
    is_active: boolean
    is_superuser: boolean
    password1: string
    password2: string
    teams: PlainDetails[]
    delete_message: string
}

const initialState: UserModalState = {
    loading: true,
    isOpen: false,
    error: null,
    user_id: null,
    first_name: '',
    last_name: '',
    email: '',
    is_staff: false,
    is_active: false,
    is_superuser: false,
    password1: '',
    password2: '',
    teams: [],
    delete_message: '',
}

export type NewUserPayload = {
    first_name: string
    last_name: string
    email: string
    is_staff: boolean
    is_active: boolean
    is_superuser: boolean
    password1: string
    password2: string
    teams: PlainDetails[]
}

export type EditUserPayload = NewUserPayload & {
    user_id: string
}


export const getUser = createAsyncThunk(
    'userModal/getUser',
    async (user_id: string, thunkAPI) => {
        try {
            return await api.get(`/API/admin/users/${user_id}/`);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveNewUser = createAsyncThunk(
    'userModal/saveNewUser',
    async (payload: NewUserPayload, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        try {
            const response = await api.post(`/API/admin/users/`, payload);
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveUser = createAsyncThunk(
    'userModal/saveUser',
    async (payload: EditUserPayload, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.patch(`/API/admin/users/${payload.user_id}/`, payload);
            dispatch(updateUser(response));
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const deleteUser = createAsyncThunk(
    'userModal/deleteUser',
    async (user_id: string, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.delete(`/API/admin/users/${user_id}/`);
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type ModalPayload = {
    user_id: string | null
}

const slice = createSlice({
    name: 'userModal',
    initialState,
    reducers: {
        closeModal: () => {
            return initialState;
        },
        openModal: (state, action: PayloadAction<ModalPayload>) => {
            if (action.payload.user_id) {
                state.user_id = action.payload.user_id;
            } else {
                state.loading = false
            }
            state.isOpen = true;
        },
        changeModal: (state, action: PayloadAction<Partial<UserModalState>>) => {
            return {
                ...state,
                ...action.payload
            };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getUser.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                Object.assign(state, action.payload);
            })
            .addCase(getUser.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(deleteUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteUser.fulfilled, () => {
                return initialState;
            })
            .addCase(deleteUser.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })

            .addCase(saveNewUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveNewUser.fulfilled, () => {
                return initialState;
            })
            .addCase(saveNewUser.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveUser.fulfilled, () => {
                return initialState;
            })
            .addCase(saveUser.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            });
    }
});

export const {
    closeModal,
    openModal,
    changeModal
} = slice.actions;

export default slice.reducer;